import React, { Fragment, ReactNode, useState, useMemo, useEffect } from 'react';
import FilterOptions from './components/FilterOptions';
import {
  employeeService,
  Employee,
  CreateEmployeeData,
  EmployeeStatus,
} from '../../api/Employee';
import { auth } from '../../api/auth';
import { getApiErrorMessage } from '../../api';
import { departamentService, Departament } from '../../api/departament';
import {
  Users,
  Search,
  Filter,
  Plus,
  Edit2,
  Trash2,
  UserPlus,
  KeyRound,
  ShieldCheck,
  Briefcase,
  Building2,
  Phone,
  Calendar,
  User,
  Mail,
  X,
  Eye,
  EyeOff,
  AlertCircle,
  IdCard,
  FileUser,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import {
  PageHeader,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Th,
  Tr,
  Td,
  StatCard,
  Button,
  Badge,
  Input,
  Select,
  Label,
  Modal,
  ConfirmModal,
} from '../../components/ui/SharedLayout';

const statusOptions = [
  { id: 'ativo', label: 'Ativo' },
  { id: 'inativo', label: 'Inativo' },
  { id: 'ferias', label: 'Férias' },
  { id: 'licenca', label: 'Licença' },
];

const accessRoleOptions = [
  { id: 'USER', label: 'Motorista' },
  { id: 'CLIENT', label: 'Supervisor' },
  { id: 'ADMIN', label: 'Administrador' },
] as const;

type AccessRole = (typeof accessRoleOptions)[number]['id'];

const defaultFormData = (): CreateEmployeeData => ({
  name: '',
  email: '',
  cpf: '',
  cargo: '',
  setor: '',
  telefone: '',
  datadeadimissão: new Date(),
  status: 'ativo',
});

const defaultAccessForm = () => ({
  employeeId: 0,
  employeeName: '',
  employeeEmail: '',
  role: 'USER' as AccessRole,
  password: '',
  confirmPassword: '',
});

const roleLabelMap: Record<AccessRole, string> = {
  ADMIN: 'Administrador',
  CLIENT: 'Supervisor',
  USER: 'Motorista',
};

const statusLabelMap: Record<EmployeeStatus, string> = {
  ativo: 'Ativo',
  inativo: 'Inativo',
  ferias: 'Férias',
  licenca: 'Licença',
};

const PASSWORD_MIN_REQUIREMENTS =
  'Mínimo de 8 caracteres, contendo apenas letras e números (sem símbolos, espaços ou acentos).';

const formatAccessStatus = (funcionario: Employee) => {
  if (!funcionario.user_id || !funcionario.user) {
    return {
      label: 'Sem acesso',
      tone: 'slate',
      className: 'bg-gray-100 text-gray-700',
    };
  }

  if (!funcionario.user.is_active) {
    return {
      label: `${roleLabelMap[funcionario.user.role]} inativo`,
      tone: 'rose',
      className: 'bg-red-100 text-red-800',
    };
  }

  if (funcionario.user.must_change_password) {
    return {
      label: `${roleLabelMap[funcionario.user.role]} · trocar senha`,
      tone: 'amber',
      className: 'bg-amber-100 text-amber-900',
    };
  }

  return {
    label: roleLabelMap[funcionario.user.role],
    tone: 'blue',
    className: 'bg-blue-100 text-blue-800',
  };
};

interface FormErrors {
  name?: string;
  email?: string;
  cpf?: string;
  cargo?: string;
  setor?: string;
  department_id?: string;
  telefone?: string;
  datadeadimissão?: string;
  status?: string;
  role?: string;
  password?: string;
  confirmPassword?: string;
}

type FieldWrapperProps = {
  children: ReactNode;
  error?: string;
  label?: ReactNode;
  htmlFor?: string;
  required?: boolean;
};

const FieldWrapper = ({ children, error, label, htmlFor, required }: FieldWrapperProps) => (
  <div className="flex flex-col gap-1">
    {label ? (
      <Label htmlFor={htmlFor} className="mb-0.5">
        <span className="inline-flex items-center gap-0.5">
          {label}
          {required ? <span className="text-rose-500 ml-0.5 font-bold leading-none">*</span> : null}
        </span>
      </Label>
    ) : null}
    {children}
    {error ? (
      <div className="flex items-center gap-1 text-xs text-rose-600">
        <AlertCircle className="h-3 w-3 flex-shrink-0" />
        <span className="leading-tight">{error}</span>
      </div>
    ) : null}
  </div>
);

const CharCounter = ({ current, max }: { current: number; max: number }) => (
  <div className={`text-[11px] font-medium ${current > max * 0.9 ? 'text-amber-600' : 'text-slate-400'}`}>
    {current}/{max}
  </div>
);

const Funcionarios = () => {
  const [funcionarios, setFuncionarios] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string[]>>({});
  const [filterModalKey, setFilterModalKey] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFuncionario, setEditingFuncionario] = useState<Employee | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [funcionarioParaDesativar, setFuncionarioParaDesativar] = useState<Employee | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 20;
  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
  const [accessMode, setAccessMode] = useState<'create' | 'update'>('create');
  const [isPasswordResetModalOpen, setIsPasswordResetModalOpen] =
    useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showResetNewPassword, setShowResetNewPassword] = useState(false);
  const [showResetConfirmPassword, setShowResetConfirmPassword] =
    useState(false);
  const [departments, setDepartments] = useState<Departament[]>([]);
  const [formData, setFormData] = useState<CreateEmployeeData>(defaultFormData);
  const [accessFormData, setAccessFormData] = useState(defaultAccessForm);
  const [createAccessOnCreate, setCreateAccessOnCreate] = useState(true);
  const [cargoSelectionMode, setCargoSelectionMode] = useState<'gestor' | 'motorista' | 'outro'>('motorista');
  const [resetPasswordForm, setResetPasswordForm] = useState({
    employeeId: 0,
    employeeName: '',
    employeeEmail: '',
    currentStatusMustChange: false,
    newPassword: '',
    confirmPassword: '',
    mustChangePassword: true,
  });

  const validateStrongPassword = (pwd: string): { ok: boolean; reason?: string } => {
    if (!pwd) return { ok: false, reason: 'Senha não pode ficar em branco.' };
    if (pwd.length < 8) return { ok: false, reason: 'Senha deve ter no mínimo 8 caracteres.' };
    if (!/^[A-Za-z0-9]+$/.test(pwd)) return { ok: false, reason: 'Senha deve conter apenas letras e números (sem símbolos, espaços ou acentos).' };
    return { ok: true };
  };

  const resetEmployeeModal = () => {
    setEditingFuncionario(null);
    setFormData(defaultFormData());
    setAccessFormData(defaultAccessForm());
    setCreateAccessOnCreate(true);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setIsModalOpen(false);
  };

  const resetAccessModal = () => {
    setAccessFormData(defaultAccessForm());
    setShowPassword(false);
    setShowConfirmPassword(false);
    setIsAccessModalOpen(false);
  };

  const resetPasswordResetModal = () => {
    setResetPasswordForm({
      employeeId: 0,
      employeeName: '',
      employeeEmail: '',
      currentStatusMustChange: false,
      newPassword: '',
      confirmPassword: '',
      mustChangePassword: true,
    });
    setShowResetNewPassword(false);
    setShowResetConfirmPassword(false);
    setIsPasswordResetModalOpen(false);
  };

  const handleOpenPasswordReset = (funcionario: Employee) => {
    setResetPasswordForm({
      employeeId: funcionario.id,
      employeeName: funcionario.name,
      employeeEmail: funcionario.email,
      currentStatusMustChange: !!funcionario.user?.must_change_password,
      newPassword: '',
      confirmPassword: '',
      mustChangePassword: true,
    });
    setShowResetNewPassword(false);
    setShowResetConfirmPassword(false);
    setIsPasswordResetModalOpen(true);
  };

  const handleResetPasswordInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setResetPasswordForm((prev) => ({ ...prev, [name]: checked }));
    } else {
      setResetPasswordForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmitResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    const trimmed = resetPasswordForm.newPassword.trim();
    const trimmedConfirm = resetPasswordForm.confirmPassword.trim();

    const strong = validateStrongPassword(trimmed);
    if (!strong.ok) {
      setError(strong.reason ?? 'Senha muito fraca.');
      return;
    }
    if (trimmed !== trimmedConfirm) {
      setError('A confirmação da senha não confere.');
      return;
    }

    setLoading(true);
    try {
      await auth.adminResetEmployeePassword({
        employeeId: resetPasswordForm.employeeId,
        newPassword: trimmed,
        confirmPassword: trimmedConfirm,
        mustChangePassword: resetPasswordForm.mustChangePassword,
      });
      await fetchFuncionarios();
      setSuccessMessage(
        resetPasswordForm.mustChangePassword
          ? 'Senha redefinida. No próximo login o funcionário será obrigado a escolher uma nova senha.'
          : 'Senha redefinida e acesso liberado imediatamente.',
      );
      resetPasswordResetModal();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao redefinir senha.'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    if (formErrors[name as keyof FormErrors]) {
      setFormErrors((prev) => ({ ...prev, [name]: undefined }));
    }

    if (name === 'cpf') {
      const digits = value.replace(/\D/g, '').slice(0, 11);
      let formatted = digits;
      if (digits.length > 3) formatted = `${digits.slice(0, 3)}.${digits.slice(3)}`;
      if (digits.length > 6) formatted = `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
      if (digits.length > 9) formatted = `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
      setFormData((prev) => ({ ...prev, cpf: formatted }));
      return;
    }

    if (name === 'telefone') {
      const digits = value.replace(/\D/g, '').slice(0, 11);
      let formatted = digits;
      if (digits.length > 2) formatted = `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
      if (digits.length > 7) formatted = `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
      setFormData((prev) => ({ ...prev, telefone: formatted }));
      return;
    }

    if (name === 'datadeadimissão') {
      setFormData((prev) => ({
        ...prev,
        datadeadimissão: new Date(value),
      }));
      return;
    }

    if (name === 'status') {
      setFormData((prev) => ({
        ...prev,
        status: value as EmployeeStatus,
      }));
      return;
    }

    if (name === 'department_id') {
      const id = value === '' || value === null || value === undefined
        ? null
        : typeof value === 'number' ? value : parseInt(value, 10);
      setFormData((prev) => ({
        ...prev,
        department_id: Number.isNaN(id) ? null : id,
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const fetchFuncionarios = async () => {
    setLoading(true);
    setError(null);
    try {
      const items = await employeeService.getAllEmployees();
      const ordenados = items
        .map((funcionario) => ({
          ...funcionario,
          datadeadimissão: new Date(funcionario.datadeadimissão),
        }))
        .sort((a, b) => b.id - a.id);
      setFuncionarios(ordenados);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao buscar funcionários.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchFuncionarios();
    departamentService.getAll().then(setDepartments).catch(() => {
      setDepartments([]);
    });
  }, []);

  const funcionariosFiltrados = useMemo(() => {
    let filtered = [...funcionarios];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (funcionario) =>
          funcionario.name.toLowerCase().includes(term) ||
          funcionario.email.toLowerCase().includes(term) ||
          funcionario.cpf.toLowerCase().includes(term) ||
          funcionario.cargo.toLowerCase().includes(term) ||
          funcionario.setor.toLowerCase().includes(term),
      );
    }

    if (Object.keys(appliedFilters).length > 0) {
      Object.entries(appliedFilters).forEach(([category, values]) => {
        if (values.length > 0) {
          filtered = filtered.filter((funcionario) => {
            if (category === 'Status') {
              return values.includes(funcionario.status);
            }
            if (category === 'Departamento') {
              return values.includes(funcionario.setor);
            }
            if (category === 'Cargo') {
              return values.includes(funcionario.cargo);
            }
            if (category === 'Acesso') {
              const accessLabel = funcionario.user?.role || 'SEM_ACESSO';
              return values.includes(accessLabel);
            }
            return true;
          });
        }
      });
    }

    return filtered;
  }, [funcionarios, searchTerm, appliedFilters]);

  const filterCategories = useMemo(() => {
    const departamentSet = new Set(funcionarios.map((f) => f.setor).filter(Boolean));
    const cargoSet = new Set(funcionarios.map((f) => f.cargo).filter(Boolean));

    return {
      Status: statusOptions,
      Departamento: Array.from(departamentSet).map((d) => ({ id: d, label: d })),
      Cargo: Array.from(cargoSet).map((c) => ({ id: c, label: c })),
      Acesso: [
        { id: 'SEM_ACESSO', label: 'Sem acesso' },
        { id: 'USER', label: 'Motorista' },
        { id: 'CLIENT', label: 'Supervisor' },
        { id: 'ADMIN', label: 'Administrador' },
      ],
    };
  }, [funcionarios]);

  const totalFiltrosAplicados = useMemo(
    () => Object.values(appliedFilters).reduce((sum, arr) => sum + arr.length, 0),
    [appliedFilters],
  );

  const handleOpenFilter = () => {
    setFilterModalKey((k) => k + 1);
    setIsFilterOpen(true);
  };

  const handleClearFilters = () => {
    setAppliedFilters({});
    setCurrentPage(1);
  };

  const totalPaginas = useMemo(() => {
    return Math.max(1, Math.ceil(funcionariosFiltrados.length / ITEMS_PER_PAGE));
  }, [funcionariosFiltrados.length]);

  const { funcionariosPaginados, pageRange } = useMemo(() => {
    const total = funcionariosFiltrados.length;
    const safePage = Math.min(currentPage, totalPaginas);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const sliced = funcionariosFiltrados.slice(startIndex, endIndex);
    return {
      funcionariosPaginados: sliced,
      pageRange: {
        start: total === 0 ? 0 : startIndex + 1,
        end: Math.min(endIndex, total),
        total,
      },
    };
  }, [funcionariosFiltrados, currentPage, totalPaginas, ITEMS_PER_PAGE]);

  const getPageNumbers = useMemo(() => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPaginas <= maxVisible + 2) {
      for (let i = 1; i <= totalPaginas; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPaginas - 1, currentPage + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPaginas - 1) pages.push('...');
    pages.push(totalPaginas);
    return pages;
  }, [currentPage, totalPaginas]);

  useEffect(() => {
    if (currentPage > totalPaginas) {
      setCurrentPage(1);
    }
  }, [funcionariosFiltrados.length, totalPaginas, currentPage]);

  const abrirConfirmacaoDesativacao = (funcionario: Employee) => {
    setFuncionarioParaDesativar(funcionario);
    setIsDeleteConfirmOpen(true);
  };

  const confirmarDesativacao = async () => {
    if (!funcionarioParaDesativar) return;

    setDeleteLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await employeeService.deleteEmployee(funcionarioParaDesativar.id);
      await fetchFuncionarios();
      setSuccessMessage('Funcionário desativado com sucesso.');
      setIsDeleteConfirmOpen(false);
      setFuncionarioParaDesativar(null);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao desativar funcionário.'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelarDesativacao = () => {
    if (deleteLoading) return;
    setIsDeleteConfirmOpen(false);
    setFuncionarioParaDesativar(null);
  };

  const validateForm = (): FormErrors => {
    const errors: FormErrors = {};

    const trimmedName = formData.name.trim();
    if (!trimmedName) {
      errors.name = 'Nome é obrigatório';
    } else if (trimmedName.length < 3) {
      errors.name = 'Nome deve ter pelo menos 3 caracteres';
    } else if (trimmedName.length > 150) {
      errors.name = 'Nome excedeu o limite de 150 caracteres';
    }

    const trimmedEmail = formData.email.trim();
    if (!trimmedEmail) {
      errors.email = 'E-mail é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      errors.email = 'Informe um e-mail válido';
    } else if (trimmedEmail.length > 254) {
      errors.email = 'E-mail excedeu o limite de 254 caracteres';
    } else {
      const emailDuplicado = funcionarios.find(
        (f) =>
          f.email.toLowerCase() === trimmedEmail.toLowerCase() &&
          f.id !== editingFuncionario?.id,
      );
      if (emailDuplicado) {
        errors.email = 'Este e-mail já está cadastrado';
      }
    }

    const cpfDigits = formData.cpf.replace(/\D/g, '');
    if (!cpfDigits) {
      errors.cpf = 'CPF é obrigatório';
    } else if (cpfDigits.length !== 11) {
      errors.cpf = 'CPF deve ter 11 dígitos';
    } else {
      const cpfDuplicado = funcionarios.find(
        (f) =>
          f.cpf.replace(/\D/g, '') === cpfDigits &&
          f.id !== editingFuncionario?.id,
      );
      if (cpfDuplicado) {
        errors.cpf = 'Este CPF já está cadastrado';
      }
    }

    const trimmedCargo = formData.cargo.trim();
    if (!trimmedCargo) {
      errors.cargo = 'Cargo é obrigatório';
    } else if (trimmedCargo.length > 100) {
      errors.cargo = 'Cargo excedeu o limite de 100 caracteres';
    }

    if (!formData.setor && !formData.department_id) {
      errors.setor = 'Selecione um departamento';
    }

    if (!formData.telefone || formData.telefone.replace(/\D/g, '').length < 10) {
      errors.telefone = 'Informe um telefone válido com DDD';
    }

    if (!formData.datadeadimissão) {
      errors.datadeadimissão = 'Data de admissão é obrigatória';
    } else if (Number.isNaN(new Date(formData.datadeadimissão).getTime())) {
      errors.datadeadimissão = 'Data de admissão inválida';
    }

    if (!formData.status) {
      errors.status = 'Status é obrigatório';
    }

    if (!editingFuncionario && createAccessOnCreate) {
      const trimmedPwd = accessFormData.password.trim();
      if (!trimmedPwd) {
        errors.password = 'Senha é obrigatória';
      } else if (trimmedPwd.length < 8) {
        errors.password = 'Senha deve ter pelo menos 8 caracteres';
      } else if (!/^[A-Za-z0-9]+$/.test(trimmedPwd)) {
        errors.password = 'Senha deve conter apenas letras e números';
      }

      if (trimmedPwd !== accessFormData.confirmPassword.trim()) {
        errors.confirmPassword = 'A confirmação da senha não confere';
      }
    }

    return errors;
  };

  const detectCargoMode = (cargo: string | undefined): 'gestor' | 'motorista' | 'outro' => {
    const c = (cargo || '').toLowerCase();
    const isGestor =
      c.includes('supervisor') ||
      c.includes('supervisora') ||
      c.includes('gestor') ||
      c.includes('gestora') ||
      c.includes('coordenador') ||
      c.includes('coordenadora') ||
      c.includes('gerente') ||
      c.includes('chefe') ||
      c.includes('chefia') ||
      c.includes('cliente') ||
      c.includes('administrador') ||
      c.includes('admin');
    const isMotorista =
      c.includes('motorista') ||
      c.includes('condutor') ||
      c.includes('operador') ||
      c.includes('entregador');
    if (isGestor) return 'gestor';
    if (isMotorista) return 'motorista';
    return 'outro';
  };

  const handleCargoModeChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const newMode = e.target.value as 'gestor' | 'motorista' | 'outro';
    setCargoSelectionMode(newMode);

    let cargoText = formData.cargo;
    let targetRole: AccessRole = accessFormData.role;

    if (newMode === 'gestor') {
      cargoText = 'Gestor/Supervisor de setor';
      targetRole = 'CLIENT';
    } else if (newMode === 'motorista') {
      cargoText = 'Motorista';
      targetRole = 'USER';
    }

    setFormData((prev) => ({ ...prev, cargo: cargoText }));
    setAccessFormData((prev) => ({ ...prev, role: targetRole }));
    void Promise.resolve().then(() => {
      setFormErrors((prev) => ({ ...prev, cargo: validateForm().cargo }));
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setLoading(true);
    try {
      const normalizedCpf = formData.cpf.replace(/\D/g, '');
      const normalizedTelefone = formData.telefone.replace(/\D/g, '');
      const normalizedDepartmentId =
        formData.department_id === undefined ||
        formData.department_id === null ||
        Number.isNaN(formData.department_id as number)
          ? null
          : Number(formData.department_id);
      const normalizedFormData = {
        ...formData,
        cpf: normalizedCpf,
        telefone: normalizedTelefone,
        department_id: normalizedDepartmentId,
      };

      if (editingFuncionario) {
        await employeeService.updateEmployee(editingFuncionario.id, normalizedFormData);
        setSuccessMessage('Funcionário atualizado com sucesso.');
      } else if (createAccessOnCreate) {
        await auth.createEmployeeWithAccess({
          name: formData.name,
          email: formData.email,
          cpf: normalizedCpf,
          cargo: formData.cargo,
          setor: formData.setor,
          department_id: normalizedDepartmentId,
          telefone: normalizedTelefone,
          datadeadimissão: formData.datadeadimissão.toISOString(),
          status: formData.status,
          role: accessFormData.role,
          password: accessFormData.password.trim(),
        });
        setSuccessMessage(
          'Funcionário e perfil de acesso criados com sucesso.',
        );
      } else {
        await employeeService.createEmployee(normalizedFormData);
        setSuccessMessage(
          'Funcionário criado com sucesso. Você pode liberar o acesso depois pela listagem.',
        );
      }

      await fetchFuncionarios();
      resetEmployeeModal();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao salvar funcionários.'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (funcionario: Employee) => {
    setEditingFuncionario(funcionario);
    setCargoSelectionMode(detectCargoMode(funcionario.cargo));
    setFormData({
      user_id: funcionario.user_id ?? null,
      department_id: funcionario.department_id ?? null,
      name: funcionario.name,
      email: funcionario.email,
      cpf: funcionario.cpf,
      cargo: funcionario.cargo,
      setor: funcionario.setor,
      telefone: funcionario.telefone,
      datadeadimissão: new Date(funcionario.datadeadimissão),
      status: funcionario.status,
    });
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setEditingFuncionario(null);
    setFormData(defaultFormData());
    setAccessFormData(defaultAccessForm());
    setCargoSelectionMode('motorista');
    setCreateAccessOnCreate(true);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setIsModalOpen(true);
  };

  const handleOpenAccessModal = (funcionario: Employee) => {
    const existingRole: AccessRole | undefined = funcionario.user?.role;
    const mode: 'create' | 'update' = funcionario.user_id && existingRole ? 'update' : 'create';
    setAccessMode(mode);

    let defaultRole: AccessRole;
    if (mode === 'update' && existingRole) {
      defaultRole = existingRole;
    } else {
      const cargo = funcionario.cargo?.toLowerCase() || '';
      const isAdmin = cargo.includes('administrador') || cargo.includes('admin');
      const isSupervisor =
        cargo.includes('supervisor') ||
        cargo.includes('supervisora') ||
        cargo.includes('gestor') ||
        cargo.includes('gestora') ||
        cargo.includes('coordenador') ||
        cargo.includes('coordenadora') ||
        cargo.includes('gerente') ||
        cargo.includes('chefe') ||
        cargo.includes('chefia') ||
        cargo.includes('cliente');
      if (isAdmin) defaultRole = 'ADMIN';
      else if (isSupervisor) defaultRole = 'CLIENT';
      else defaultRole = 'USER';
    }

    setAccessFormData({
      employeeId: funcionario.id,
      employeeName: funcionario.name,
      employeeEmail: funcionario.email,
      role: defaultRole,
      password: '',
      confirmPassword: '',
    });
    setShowPassword(false);
    setShowConfirmPassword(false);
    setIsAccessModalOpen(true);
  };

  const handleAccessInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setAccessFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateAccess = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (accessMode === 'update') {
      setLoading(true);
      try {
        await auth.updateEmployeeRole({
          employeeId: accessFormData.employeeId,
          role: accessFormData.role,
        });
        await fetchFuncionarios();
        setSuccessMessage('Perfil de acesso alterado com sucesso.');
        resetAccessModal();
      } catch (err) {
        setError(getApiErrorMessage(err, 'Erro ao atualizar perfil de acesso.'));
      } finally {
        setLoading(false);
      }
      return;
    }

    const trimmedPassword = accessFormData.password.trim();
    if (trimmedPassword.length < 8) {
      setError('A senha deve ter pelo menos 8 caracteres.');
      return;
    }
    if (!/^[A-Za-z0-9]+$/.test(trimmedPassword)) {
      setError('A senha deve conter apenas letras e números (sem símbolos, espaços ou acentos).');
      return;
    }

    if (trimmedPassword !== accessFormData.confirmPassword.trim()) {
      setError('A confirmação da senha não confere.');
      return;
    }

    setLoading(true);
    try {
      await auth.createEmployeeAccess({
        employeeId: accessFormData.employeeId,
        role: accessFormData.role,
        password: trimmedPassword,
      });
      await fetchFuncionarios();
      setSuccessMessage('Perfil de acesso criado com sucesso.');
      resetAccessModal();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao criar perfil de acesso.'));
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilter = (selectedFilters: Record<string, string[]>) => {
    setAppliedFilters(selectedFilters);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };


  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-7">
        <PageHeader
          title="Funcionários"
          hideTitleInBody
        />
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <p className="text-[15px] font-semibold leading-7 tracking-tight text-slate-800 sm:text-[17px]">
              Gerencie a equipe de colaboradores
            </p>
            <h1 className="mt-1 text-[22px] font-bold leading-tight tracking-tight text-slate-900 sm:text-[26px]">
              Cadastre, edite e remova colaboradores.
              <span className="text-slate-500 font-semibold text-[18px] sm:text-[20px]">
                {' '}Libere ou redefina o acesso ao sistema nesta mesma tela.
              </span>
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap shrink-0">
            <Button
              type="button"
              variant="secondary"
              onClick={handleOpenFilter}
              className={totalFiltrosAplicados > 0 ? '!bg-blue-50 !border-blue-200 !text-blue-700 hover:!bg-blue-100' : ''}
            >
              <Filter className="h-4 w-4 stroke-[1.8]" />
              Filtros
              {totalFiltrosAplicados > 0 ? (
                <span className="ml-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-blue-600 px-1.5 text-[11px] font-bold text-white">
                  {totalFiltrosAplicados}
                </span>
              ) : null}
            </Button>
            {totalFiltrosAplicados > 0 ? (
              <Button
                type="button"
                variant="secondary"
                onClick={handleClearFilters}
              >
                <X className="h-4 w-4 stroke-[1.8]" />
                Limpar
              </Button>
            ) : null}
            <Button
              type="button"
              variant="primary"
              onClick={handleCreate}
              disabled={loading}
            >
              <Plus className="h-4 w-4 stroke-[2.2]" />
              Novo funcionário
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4 mb-7">
          <StatCard
            icon={<Users className="h-5 w-5 stroke-[1.8]" />}
            label="Total de Funcionários"
            value={funcionarios.length}
            trend={<span>Cadastrados no sistema</span>}
            variant="deep"
          />
          <StatCard
            icon={<UserPlus className="h-5 w-5 stroke-[1.8]" />}
            label="Ativos"
            value={funcionarios.filter(f => f.status === 'ativo').length}
            trend={<span>Status ativo</span>}
            variant="primary"
          />
          <StatCard
            icon={<ShieldCheck className="h-5 w-5 stroke-[1.8]" />}
            label="Com Acesso"
            value={funcionarios.filter(f => f.user_id && f.user?.is_active).length}
            trend={<span>Permissão liberada</span>}
            variant="sky"
          />
          <StatCard
            icon={<KeyRound className="h-5 w-5 stroke-[1.8]" />}
            label="Precisam Trocar Senha"
            value={funcionarios.filter(f => f.user?.must_change_password).length}
            trend={<span>Usuários com senha pendente</span>}
            variant="rich"
          />
        </div>

        {error && (
          <div className="mb-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="mb-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            {successMessage}
          </div>
        )}

        <SectionCard>
          <CardHeader>
            <div>
              <h2 className="text-[15px] font-bold tracking-tight text-slate-900">
                Colaboradores
              </h2>
              <p className="text-[13px] text-slate-500 mt-0.5">
                {loading
                  ? 'Carregando registros...'
                  : pageRange.total === 0
                    ? 'Nenhum funcionário encontrado'
                    : `${pageRange.start} a ${pageRange.end} de ${pageRange.total} colaboradores listados (total cadastrado: ${funcionarios.length})`}
              </p>
            </div>
            <div className="w-full sm:w-[380px] shrink-0 relative">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 stroke-[1.8] text-slate-400" aria-hidden="true" />
              <Input
                type="text"
                name="search-employees"
                placeholder="Buscar nome, e-mail, CPF, cargo ou setor..."
                value={searchTerm}
                onChange={handleSearchChange}
                disabled={loading}
                className="!pl-10"
              />
            </div>
          </CardHeader>

          <CardBody className="!py-3">
            {loading && funcionarios.length === 0 ? (
              <div className="flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-sm font-medium text-slate-600">
                  Carregando funcionários...
                </span>
              </div>
            ) : (
              <Fragment>
                <ModernTable>
                <Thead>
                  <Tr>
                    <Th>Colaborador</Th>
                    <Th>Telefone</Th>
                    <Th>Cargo</Th>
                    <Th>Setor</Th>
                    <Th>Admissão</Th>
                    <Th>Status</Th>
                    <Th>Acesso</Th>
                    <Th>Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {funcionariosFiltrados.length === 0 ? (
                    <tr>
                      <td
                        colSpan={8}
                        className="px-5 py-14 text-center text-sm text-slate-500"
                      >
                        Nenhum funcionário encontrado
                      </td>
                    </tr>
                  ) : (
                    funcionariosPaginados.map((funcionario) => {
                      const accessStatus = formatAccessStatus(funcionario);
                      const statusBadge =
                        funcionario.status === 'ativo' ? (
                          <Badge tone="emerald">{statusLabelMap[funcionario.status]}</Badge>
                        ) : funcionario.status === 'inativo' ? (
                          <Badge tone="slate">{statusLabelMap[funcionario.status]}</Badge>
                        ) : (
                          <Badge tone="amber">{statusLabelMap[funcionario.status]}</Badge>
                        );
                      return (
                        <Tr key={funcionario.id}>
                          <Td>
                            <div className="flex items-center gap-3">
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 text-white shadow-[0_6px_16px_-8px_rgba(37,99,235,0.6)]">
                                <User className="h-[18px] w-[18px] stroke-[1.9]" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="truncate font-semibold text-slate-900">
                                  {funcionario.name}
                                </div>
                                <div className="mt-0.5 text-[12px] text-slate-500">
                                  <span className="truncate" title={funcionario.email}>
                                    {funcionario.email}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <span
                              className="block truncate font-medium text-slate-700"
                              title={funcionario.telefone || ''}
                            >
                              {funcionario.telefone || '-'}
                            </span>
                          </Td>
                          <Td>
                            <span
                              className="block truncate font-medium text-slate-700"
                              title={funcionario.cargo}
                            >
                              {funcionario.cargo || '-'}
                            </span>
                          </Td>
                          <Td>
                            <span
                              className="block truncate font-medium text-slate-700"
                              title={funcionario.setor}
                            >
                              {funcionario.setor || '-'}
                            </span>
                          </Td>
                          <Td>
                            <span className="font-medium text-slate-700 tabular-nums">
                              {new Date(funcionario.datadeadimissão).toLocaleDateString('pt-BR')}
                            </span>
                          </Td>
                          <Td>{statusBadge}</Td>
                          <Td>
                            <Badge
                              tone={accessStatus.tone as any}
                              className="!text-[12px] !max-w-[170px]"
                              title={accessStatus.label}
                            >
                              <span className="block truncate">
                                {accessStatus.label}
                              </span>
                            </Badge>
                          </Td>
                          <Td>
                            <div className="inline-flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => handleOpenAccessModal(funcionario)}
                                className="inline-flex h-9 px-2.5 items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white text-[12px] font-semibold text-slate-700 transition-all duration-200 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:ring-offset-1"
                                title="Gerenciar acesso"
                                aria-label="Gerenciar acesso"
                              >
                                <ShieldCheck className="h-[15px] w-[15px] stroke-[1.8]" />
                                Acesso
                              </button>
                              <button
                                type="button"
                                onClick={() => handleOpenPasswordReset(funcionario)}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-amber-300 hover:bg-amber-50 hover:text-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-400/40 focus:ring-offset-1"
                                title="Redefinir senha"
                                aria-label="Redefinir senha"
                              >
                                <KeyRound className="h-[15px] w-[15px] stroke-[1.8]" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleEdit(funcionario)}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                                title="Editar funcionário"
                                aria-label="Editar funcionário"
                              >
                                <Edit2 className="h-[15px] w-[15px] stroke-[1.8]" />
                              </button>
                              <button
                                type="button"
                                onClick={() => abrirConfirmacaoDesativacao(funcionario)}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-400/40 focus:ring-offset-1"
                                title="Remover / Desativar"
                                aria-label="Remover ou desativar funcionário"
                              >
                                <Trash2 className="h-[15px] w-[15px] stroke-[1.8]" />
                              </button>
                            </div>
                          </Td>
                        </Tr>
                      );
                    })
                  )}
                </Tbody>
              </ModernTable>

                {pageRange.total > 0 ? (
                  <div className="mt-5 flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-[13px] font-medium text-slate-500">
                      Mostrando <span className="font-semibold text-slate-700">{pageRange.start}-{pageRange.end}</span> de <span className="font-semibold text-slate-700">{pageRange.total}</span> resultados
                    </div>
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        aria-label="Primeira página"
                        className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronsLeft className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        aria-label="Página anterior"
                        className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronLeft className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <div className="inline-flex items-center gap-1 mx-1">
                        {getPageNumbers.map((n, idx) =>
                          n === '...' ? (
                            <span
                              key={`dots-${idx}`}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 text-[13px]"
                            >
                              …
                            </span>
                          ) : (
                            <button
                              key={n}
                              type="button"
                              onClick={() => setCurrentPage(n)}
                              aria-label={`Ir para página ${n}`}
                              className={`inline-flex h-9 min-w-[36px] items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all duration-200 ${
                                n === currentPage
                                  ? 'bg-blue-600 text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)]'
                                  : 'border border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                              }`}
                            >
                              {n}
                            </button>
                          ),
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.min(totalPaginas, p + 1))}
                        disabled={currentPage === totalPaginas}
                        aria-label="Próxima página"
                        className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronRight className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentPage(totalPaginas)}
                        disabled={currentPage === totalPaginas}
                        aria-label="Última página"
                        className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronsRight className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                    </div>
                  </div>
                ) : null}
              </Fragment>
            )}
          </CardBody>
        </SectionCard>

        {isFilterOpen && (
          <FilterOptions
            key={filterModalKey}
            isOpen={isFilterOpen}
            onClose={() => setIsFilterOpen(false)}
            onApplyFilter={handleApplyFilter}
            filterCategories={filterCategories}
            initialFilters={appliedFilters}
          />
        )}

      </div>

            {isModalOpen && (
        <Modal
          onClose={resetEmployeeModal}
          maxWidthClass="max-w-2xl"
          title={
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 via-indigo-500 to-indigo-600 text-white shadow-[0_6px_16px_-8px_rgba(99,102,241,0.7)]">
              <UserPlus className="h-[18px] w-[18px]" />
            </div>
            <div>
              <h3 className="text-[15px] font-bold tracking-tight text-slate-900 leading-tight">
                {editingFuncionario ? 'Editar colaborador' : 'Novo colaborador'}
              </h3>
              <p className="text-[12px] font-medium text-slate-500 mt-0.5 leading-snug">
                {editingFuncionario
                  ? 'Atualize os dados cadastrais e mantenha sua equipe organizada'
                  : 'Preencha os campos abaixo para cadastrar um novo colaborador'}
              </p>
            </div>
          </div>
        }
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={resetEmployeeModal}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              form="employee-form"
              variant="primary"
              disabled={loading}
            >
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                  Salvando...
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5">
                  {editingFuncionario ? (
                    <Edit2 className="h-4 w-4 stroke-[1.8]" />
                  ) : (
                    <UserPlus className="h-4 w-4 stroke-[1.8]" />
                  )}
                  {editingFuncionario ? 'Salvar alterações' : 'Cadastrar colaborador'}
                </span>
              )}
            </Button>
          </>
        }
      >
        <form
          id="employee-form"
          onSubmit={(e) => void handleSubmit(e)}
          className="flex flex-col gap-6"
          noValidate
        >
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <div className="h-7 w-1.5 shrink-0 rounded-full bg-gradient-to-b from-blue-500 via-blue-500 to-indigo-500" />
              <h4 className="text-[13.5px] font-bold tracking-tight text-slate-800 uppercase">
                Dados pessoais
              </h4>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <FieldWrapper
                label={
                  <span className="inline-flex items-center gap-1.5">
                    <FileUser className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                    Nome completo
                  </span>
                }
                required
                htmlFor="emp-name"
                error={formErrors.name}
              >
                <Input
                  id="emp-name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange}
                  onBlur={() =>
                    setFormErrors((prev) => ({
                      ...prev,
                      name: validateForm().name,
                    }))
                  }
                  placeholder="Nome e sobrenome do colaborador"
                  maxLength={150}
                  aria-invalid={!!formErrors.name}
                />
                <CharCounter current={formData.name.length} max={150} />
              </FieldWrapper>

              <FieldWrapper
                label={
                  <span className="inline-flex items-center gap-1.5">
                    <Mail className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                    E-mail
                  </span>
                }
                required
                htmlFor="emp-email"
                error={formErrors.email}
              >
                <Input
                  id="emp-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={() =>
                    setFormErrors((prev) => ({
                      ...prev,
                      email: validateForm().email,
                    }))
                  }
                  placeholder="colaborador@empresa.com.br"
                  maxLength={254}
                  aria-invalid={!!formErrors.email}
                />
                <CharCounter current={formData.email.length} max={254} />
              </FieldWrapper>

              <FieldWrapper
                label={
                  <span className="inline-flex items-center gap-1.5">
                    <IdCard className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                    CPF
                  </span>
                }
                required
                htmlFor="emp-cpf"
                error={formErrors.cpf}
              >
                <Input
                  id="emp-cpf"
                  name="cpf"
                  type="text"
                  value={formData.cpf}
                  onChange={(e) => {
                    const apenasNumeros = e.target.value.replace(/\D/g, '').slice(0, 11);
                    handleInputChange({
                      target: { name: 'cpf', value: apenasNumeros },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }}
                  onBlur={() =>
                    setFormErrors((prev) => ({
                      ...prev,
                      cpf: validateForm().cpf,
                    }))
                  }
                  placeholder="000.000.000-00 (apenas números)"
                  inputMode="numeric"
                  maxLength={14}
                  aria-invalid={!!formErrors.cpf}
                />
                <CharCounter current={formData.cpf.replace(/\D/g, '').length} max={11} />
              </FieldWrapper>

              <FieldWrapper
                label={
                  <span className="inline-flex items-center gap-1.5">
                    <Phone className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                    Telefone / WhatsApp
                  </span>
                }
                htmlFor="emp-telefone"
                error={formErrors.telefone}
              >
                <Input
                  id="emp-telefone"
                  name="telefone"
                  type="tel"
                  value={formData.telefone}
                  onChange={(e) => {
                    const apenasNumeros = e.target.value.replace(/\D/g, '').slice(0, 11);
                    handleInputChange({
                      target: { name: 'telefone', value: apenasNumeros },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }}
                  onBlur={() =>
                    setFormErrors((prev) => ({
                      ...prev,
                      telefone: validateForm().telefone,
                    }))
                  }
                  placeholder="(11) 90000-0000 (com DDD)"
                  inputMode="tel"
                  maxLength={15}
                  aria-invalid={!!formErrors.telefone}
                />
                <CharCounter current={formData.telefone.replace(/\D/g, '').length} max={11} />
              </FieldWrapper>
            </div>
          </div>

          <div className="mx-0 -my-1 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <div className="h-7 w-1.5 shrink-0 rounded-full bg-gradient-to-b from-emerald-500 via-emerald-500 to-teal-500" />
              <h4 className="text-[13.5px] font-bold tracking-tight text-slate-800 uppercase">
                Cargo e alocação
              </h4>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <FieldWrapper
                label={
                  <span className="inline-flex items-center gap-1.5">
                    <Briefcase className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                    Cargo / Perfil
                  </span>
                }
                required
                htmlFor="emp-cargo-mode"
                error={formErrors.cargo}
              >
                <select
                  id="emp-cargo-mode"
                  name="cargoSelectionMode"
                  value={cargoSelectionMode}
                  onChange={handleCargoModeChange}
                  className="flex h-10 w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-[14px] font-medium text-slate-900 shadow-sm transition-all duration-200 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/25 disabled:cursor-not-allowed disabled:opacity-60"
                  required
                >
                  <option value="motorista">🧍 Motorista (perfil: Usuário / Motorista)</option>
                  <option value="gestor">👔 Gestor / Supervisor de setor (perfil: Cliente / Gestor)</option>
                  <option value="outro">✏️ Outro cargo (digitar livremente)</option>
                </select>

                {cargoSelectionMode === 'outro' && (
                  <div className="mt-2.5">
                    <Input
                      id="emp-cargo"
                      name="cargo"
                      type="text"
                      value={formData.cargo}
                      onChange={handleInputChange}
                      onBlur={() =>
                        setFormErrors((prev) => ({
                          ...prev,
                          cargo: validateForm().cargo,
                        }))
                      }
                      placeholder="Digite o cargo (ex: Analista RH, Mecânico, Secretária...)"
                      maxLength={100}
                      aria-invalid={!!formErrors.cargo}
                    />
                    <CharCounter current={formData.cargo.length} max={100} />
                  </div>
                )}

                {cargoSelectionMode !== 'outro' && (
                  <div className="mt-2.5 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-[12.5px] leading-relaxed text-slate-600">
                    <span className="font-semibold text-slate-900">Cargo salvo:</span>{' '}
                    <span className="font-medium text-slate-800">{formData.cargo || (cargoSelectionMode === 'gestor' ? 'Gestor/Supervisor de setor' : 'Motorista')}</span>
                    <span className="mx-1.5 text-slate-300">•</span>
                    <span className="font-semibold text-slate-900">Perfil de acesso:</span>{' '}
                    <span className={`font-bold ${cargoSelectionMode === 'gestor' ? 'text-sky-700' : 'text-slate-700'}`}>
                      {cargoSelectionMode === 'gestor' ? 'Supervisor (Cliente) — Painel /client' : 'Motorista (Usuário) — Painel /user'}
                    </span>
                  </div>
                )}
              </FieldWrapper>

              <FieldWrapper
                label={
                  <span className="inline-flex items-center gap-1.5">
                    <Building2 className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                    Departamento
                  </span>
                }
                required
                htmlFor="emp-department"
                error={formErrors.department_id ?? formErrors.setor}
              >
                <Select
                  id="emp-department"
                  name="department_id"
                  value={formData.department_id?.toString() ?? ''}
                  onChange={handleInputChange}
                  onBlur={() =>
                    setFormErrors((prev) => ({
                      ...prev,
                      department_id: validateForm().department_id ?? validateForm().setor,
                    }))
                  }
                  aria-invalid={!!(formErrors.department_id ?? formErrors.setor)}
                >
                  <option value="">Selecione um departamento</option>
                  {departments.map((depto) => (
                    <option key={depto.id} value={depto.id}>
                      {depto.name}
                    </option>
                  ))}
                </Select>
              </FieldWrapper>

              <FieldWrapper
                label={
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                    Data de admissão
                  </span>
                }
                required
                htmlFor="emp-admissao"
                error={formErrors.datadeadimissão}
              >
                <Input
                  id="emp-admissao"
                  name="datadeadimissão"
                  type="date"
                  value={
                    formData.datadeadimissão instanceof Date &&
                    !Number.isNaN(formData.datadeadimissão.getTime())
                      ? new Date(
                          formData.datadeadimissão.getTime() -
                            formData.datadeadimissão.getTimezoneOffset() * 60000,
                        )
                          .toISOString()
                          .split('T')[0]
                      : ''
                  }
                  onChange={(e) =>
                    handleInputChange({
                      target: {
                        name: 'datadeadimissão',
                        value: e.target.value ? new Date(e.target.value) : new Date(),
                      },
                    } as unknown as React.ChangeEvent<HTMLInputElement>)
                  }
                  onBlur={() =>
                    setFormErrors((prev) => ({
                      ...prev,
                      datadeadimissão: validateForm().datadeadimissão,
                    }))
                  }
                  aria-invalid={!!formErrors.datadeadimissão}
                />
              </FieldWrapper>

              <FieldWrapper
                label={
                  <span className="inline-flex items-center gap-1.5">
                    <Users className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                    Status
                  </span>
                }
                required
                htmlFor="emp-status"
                error={formErrors.status}
              >
                <Select
                  id="emp-status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  onBlur={() =>
                    setFormErrors((prev) => ({
                      ...prev,
                      status: validateForm().status,
                    }))
                  }
                  aria-invalid={!!formErrors.status}
                >
                  <option value="">Selecione um status</option>
                  {statusOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </FieldWrapper>
            </div>
          </div>

          <div className="mx-0 -my-1 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <div className="h-7 w-1.5 shrink-0 rounded-full bg-gradient-to-b from-violet-500 via-purple-500 to-fuchsia-500" />
              <h4 className="text-[13.5px] font-bold tracking-tight text-slate-800 uppercase">
                Acesso ao sistema
              </h4>
            </div>

            {!editingFuncionario ? (
              <label
                htmlFor="create-access-toggle"
                className="group flex cursor-pointer items-start justify-between gap-4 rounded-2xl border border-violet-200/70 bg-gradient-to-br from-violet-50/60 via-white to-indigo-50/40 p-4.5 shadow-sm transition-all duration-200 hover:border-violet-300 hover:shadow-md active:scale-[0.998]"
              >
                <div className="flex items-start gap-3 min-w-0">
                  <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-[0_8px_18px_-8px_rgba(139,92,246,0.55)]">
                    <ShieldCheck className="h-5 w-5 stroke-[1.8]" />
                  </div>
                  <div className="min-w-0 space-y-1">
                    <p className="font-bold text-[14px] tracking-tight text-slate-900 leading-tight">
                      Criar acesso agora
                    </p>
                    <p className="text-[12.5px] leading-relaxed text-slate-600">
                      Libere o login imediatamente após cadastrar. O colaborador receberá uma
                      senha temporária que deverá ser alterada no primeiro acesso.
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 h-full items-center pt-0.5">
                  <div
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-violet-500/40 focus:ring-offset-2 ${
                      createAccessOnCreate ? 'bg-violet-600' : 'bg-slate-200'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white ring-0 shadow-sm transition duration-200 ease-in-out ${
                        createAccessOnCreate ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                    <input
                      id="create-access-toggle"
                      name="createAccessOnCreate"
                      type="checkbox"
                      className="sr-only"
                      checked={createAccessOnCreate}
                      onChange={(e) => setCreateAccessOnCreate(e.target.checked)}
                    />
                  </div>
                </div>
              </label>
            ) : editingFuncionario.user_id ? (
              <div className="rounded-2xl border border-emerald-200/70 bg-gradient-to-br from-emerald-50/70 via-white to-teal-50/50 p-4.5 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-[0_8px_18px_-8px_rgba(16,185,129,0.55)]">
                    <ShieldCheck className="h-5 w-5 stroke-[1.8]" />
                  </div>
                  <div className="min-w-0 space-y-1 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-bold text-[14px] tracking-tight text-emerald-900 leading-tight">
                        Perfil de acesso existente
                      </p>
                      <Badge tone="emerald" className="text-[10.5px]">
                        Ativo
                      </Badge>
                    </div>
                    <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 text-[12.5px] leading-relaxed text-emerald-900/80">
                      <div className="flex gap-1.5 truncate">
                        <span className="font-semibold shrink-0">Email:</span>
                        <span className="truncate">{editingFuncionario.email}</span>
                      </div>
                      <div className="flex gap-1.5 truncate">
                        <span className="font-semibold shrink-0">Perfil:</span>
                        <span className="capitalize truncate">
                          {editingFuncionario.user?.role === 'ADMIN'
                            ? 'Administrador'
                            : editingFuncionario.user?.role === 'CLIENT'
                              ? 'Supervisor'
                              : 'Motorista'}
                        </span>
                      </div>
                    </div>
                    <p className="text-[12px] leading-relaxed text-slate-500 pt-0.5">
                      Utilize os botões{' '}
                      <span className="font-semibold">Acesso</span> ou{' '}
                      <span className="font-semibold">Redefinir senha</span> na tabela para
                      gerenciar as credenciais.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-slate-200/80 bg-gradient-to-br from-slate-50/70 via-white to-slate-50/50 p-4.5 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-slate-400 to-slate-500 text-white shadow-[0_8px_18px_-8px_rgba(100,116,139,0.45)]">
                    <User className="h-5 w-5 stroke-[1.8]" />
                  </div>
                  <div className="min-w-0 space-y-1">
                    <p className="font-bold text-[14px] tracking-tight text-slate-800 leading-tight">
                      Sem acesso cadastrado
                    </p>
                    <p className="text-[12.5px] leading-relaxed text-slate-500">
                      Após salvar, clique em{' '}
                      <span className="font-semibold">Acesso</span> na linha do colaborador para
                      liberar o login no sistema.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {!editingFuncionario && createAccessOnCreate && (
              <div className="grid grid-cols-1 gap-5 animate-fadeIn md:grid-cols-2 pt-1">
                <FieldWrapper
                  label={
                    <span className="inline-flex items-center gap-1.5">
                      <KeyRound className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                      Senha temporária
                    </span>
                  }
                  required
                  htmlFor="emp-password"
                  error={formErrors.password}
                >
                  <div className="relative">
                    <Input
                      id="emp-password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={accessFormData.password}
                      onChange={handleAccessInputChange}
                      onBlur={() =>
                        setFormErrors((prev) => ({
                          ...prev,
                          password: validateForm().password,
                        }))
                      }
                      placeholder="Mínimo 8 caracteres (letras e números)"
                      className="!pr-11"
                      autoComplete="new-password"
                      minLength={8}
                      maxLength={64}
                      aria-invalid={!!formErrors.password}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute inset-y-0 right-0 flex h-full w-10 items-center justify-center rounded-r-lg text-slate-400 transition-colors hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                      aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    >
                      {showPassword ? (
                        <EyeOff className="h-[17px] w-[17px] stroke-[1.8]" />
                      ) : (
                        <Eye className="h-[17px] w-[17px] stroke-[1.8]" />
                      )}
                    </button>
                  </div>
                  <CharCounter current={accessFormData.password.length} max={64} />
                </FieldWrapper>

                <FieldWrapper
                  label={
                    <span className="inline-flex items-center gap-1.5">
                      <ShieldCheck className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                      Confirme a senha
                    </span>
                  }
                  required
                  htmlFor="emp-confirm-password"
                  error={formErrors.confirmPassword}
                >
                  <div className="relative">
                    <Input
                      id="emp-confirm-password"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={accessFormData.confirmPassword}
                      onChange={handleAccessInputChange}
                      onBlur={() =>
                        setFormErrors((prev) => ({
                          ...prev,
                          confirmPassword: validateForm().confirmPassword,
                        }))
                      }
                      placeholder="Repita a mesma senha"
                      className="!pr-11"
                      autoComplete="new-password"
                      minLength={8}
                      maxLength={64}
                      aria-invalid={!!formErrors.confirmPassword}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      className="absolute inset-y-0 right-0 flex h-full w-10 items-center justify-center rounded-r-lg text-slate-400 transition-colors hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                      aria-label={
                        showConfirmPassword ? 'Ocultar confirmação' : 'Mostrar confirmação'
                      }
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-[17px] w-[17px] stroke-[1.8]" />
                      ) : (
                        <Eye className="h-[17px] w-[17px] stroke-[1.8]" />
                      )}
                    </button>
                  </div>
                  <CharCounter current={accessFormData.confirmPassword.length} max={64} />
                </FieldWrapper>
              </div>
            )}
          </div>
        </form>
        </Modal>
      )}

      {isAccessModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-60 px-4 py-6">
          <div className="flex min-h-full items-center justify-center">
            <div className="relative max-h-[90vh] w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl animate-fadeIn">
            <button
              onClick={resetAccessModal}
              className="absolute top-4 right-4 text-gray-500 hover:text-black text-2xl focus:outline-none"
              aria-label="Fechar"
              type="button"
            >
              &times;
            </button>
            <div className="max-h-[90vh] overflow-y-auto p-8">
            <h2 className="mb-2 text-center text-2xl font-bold text-black">
              {accessMode === 'update' ? 'Alterar perfil de acesso' : 'Criar perfil de acesso'}
            </h2>
            <p className="mb-6 text-center text-sm text-gray-500">
              {accessMode === 'update'
                ? 'Ajuste o nível de permissão deste funcionário no sistema. A senha atual é preservada.'
                : 'O login deste funcionário será criado com a estrutura de autenticação atual do sistema.'}
            </p>

            <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700 space-y-1">
              <div>
                <strong>Funcionário:</strong> {accessFormData.employeeName}
              </div>
              <div>
                <strong>E-mail de acesso:</strong> {accessFormData.employeeEmail}
              </div>
              {accessMode === 'update' && (
                <div>
                  <strong>Perfil atual:</strong>{' '}
                  <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-[11.5px] font-bold text-slate-700 ring-1 ring-inset ring-slate-200">
                    {(() => {
                      const current = funcionarios.find((f) => f.id === accessFormData.employeeId)?.user?.role;
                      return current === 'ADMIN' ? 'Administrador' : current === 'CLIENT' ? 'Supervisor (Cliente)' : 'Motorista (Usuário)';
                    })()}
                  </span>
                </div>
              )}
            </div>

            <form onSubmit={handleCreateAccess} className="space-y-5">
              <div className="flex flex-col">
                <label className="mb-1 text-sm font-semibold text-black">Perfil de acesso</label>
                <select
                  name="role"
                  value={accessFormData.role}
                  onChange={handleAccessInputChange}
                  className="px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-400 focus:outline-none transition text-black"
                  required
                >
                  {accessRoleOptions.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>

              {accessMode === 'create' && (
                <>
                  <div className="flex flex-col">
                    <label className="mb-1 text-sm font-semibold text-black">Senha</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={accessFormData.password}
                        onChange={handleAccessInputChange}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-400 focus:outline-none transition text-black pr-12"
                        placeholder="Mínimo de 8 caracteres"
                        autoComplete="new-password"
                        required
                        minLength={8}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500"
                        aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <label className="mb-1 text-sm font-semibold text-black">
                      Confirmação da senha
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        value={accessFormData.confirmPassword}
                        onChange={handleAccessInputChange}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-400 focus:outline-none transition text-black pr-12"
                        placeholder="Repita a senha"
                        autoComplete="new-password"
                        required
                        minLength={8}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500"
                        aria-label={
                          showConfirmPassword
                            ? 'Ocultar confirmação da senha'
                            : 'Mostrar confirmação da senha'
                        }
                      >
                        {showConfirmPassword ? (
                          <EyeOff size={18} />
                        ) : (
                          <Eye size={18} />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                    O backend exige senha com no mínimo 8 caracteres e aplica bloqueio temporário
                    após repetidas tentativas de login com falha.
                  </div>
                </>
              )}

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={resetAccessModal}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-black hover:bg-gray-50 font-semibold transition"
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
                  disabled={loading}
                >
                  {loading
                    ? accessMode === 'update'
                      ? 'Salvando...'
                      : 'Criando...'
                    : accessMode === 'update'
                      ? 'Salvar alterações'
                      : 'Criar acesso'}
                </button>
              </div>
            </form>
            </div>
            </div>
          </div>
        </div>
      )}

      {isPasswordResetModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-60 px-4 py-6">
          <div className="flex min-h-full items-center justify-center">
            <div className="relative max-h-[90vh] w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl animate-fadeIn">
            <button
              onClick={resetPasswordResetModal}
              className="absolute top-4 right-4 text-gray-500 hover:text-black text-2xl focus:outline-none"
              aria-label="Fechar"
              type="button"
            >
              &times;
            </button>
            <div className="max-h-[90vh] overflow-y-auto p-8">
            <h2 className="mb-2 text-center text-2xl font-bold text-black">
              Redefinir senha do funcionário
            </h2>
            <p className="mb-6 text-center text-sm text-gray-500">
              Ajuste feita pelo administrador. O funcionário receberá nova senha e, por padrão,
              terá que trocá-la no próximo login.
            </p>

            <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700 space-y-1">
              <div>
                <strong>Funcionário:</strong> {resetPasswordForm.employeeName}
              </div>
              <div>
                <strong>E-mail de acesso:</strong> {resetPasswordForm.employeeEmail}
              </div>
              <div>
                <strong>Estado atual:</strong>{' '}
                {resetPasswordForm.currentStatusMustChange ? (
                  <span className="text-amber-800">Pendente de troca de senha desde a última criação/redefinição.</span>
                ) : (
                  <span className="text-green-800">Senha utilizada pelo menos uma vez.</span>
                )}
              </div>
            </div>

            <form onSubmit={handleSubmitResetPassword} className="space-y-5">
              <div className="flex flex-col">
                <label className="mb-1 text-sm font-semibold text-black">Nova senha</label>
                <div className="relative">
                  <input
                    type={showResetNewPassword ? 'text' : 'password'}
                    name="newPassword"
                    value={resetPasswordForm.newPassword}
                    onChange={handleResetPasswordInputChange}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-400 focus:outline-none transition text-black pr-12"
                    placeholder="12+ caracteres com letra, número e símbolo"
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetNewPassword((prev) => !prev)}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500"
                    aria-label={showResetNewPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showResetNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="flex flex-col">
                <label className="mb-1 text-sm font-semibold text-black">
                  Confirme a nova senha
                </label>
                <div className="relative">
                  <input
                    type={showResetConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={resetPasswordForm.confirmPassword}
                    onChange={handleResetPasswordInputChange}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-400 focus:outline-none transition text-black pr-12"
                    placeholder="Repita a senha"
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetConfirmPassword((prev) => !prev)}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500"
                    aria-label={
                      showResetConfirmPassword
                        ? 'Ocultar confirmação'
                        : 'Mostrar confirmação'
                    }
                  >
                    {showResetConfirmPassword ? (
                      <EyeOff size={18} />
                    ) : (
                      <Eye size={18} />
                    )}
                  </button>
                </div>
              </div>

              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <label className="inline-flex items-start gap-3 text-sm">
                  <input
                    type="checkbox"
                    name="mustChangePassword"
                    checked={resetPasswordForm.mustChangePassword}
                    onChange={handleResetPasswordInputChange}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-blue-900">
                    <strong>Exigir troca de senha no próximo login.</strong>
                    <br />
                    <span className="text-xs text-blue-800">
                      Recomendado quando o administrador gera a senha temporária e o próprio
                      funcionário deve escolher sua senha definitiva.
                    </span>
                  </span>
                </label>
              </div>

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-xs text-gray-600">
                {PASSWORD_MIN_REQUIREMENTS}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={resetPasswordResetModal}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-black hover:bg-gray-50 font-semibold transition"
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
                  disabled={loading}
                >
                  {loading ? 'Redefinindo...' : 'Redefinir senha'}
                </button>
              </div>
            </form>
            </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={isDeleteConfirmOpen}
        title={
          funcionarioParaDesativar
            ? `Desativar ${funcionarioParaDesativar.name}?`
            : 'Desativar colaborador?'
        }
        description={
          funcionarioParaDesativar ? (
            <span>
              Você está prestes a desativar{' '}
              <strong className="font-semibold text-slate-700">
                {funcionarioParaDesativar.name}
              </strong>
              {' '}(CPF: <span className="font-mono font-semibold text-slate-700">{funcionarioParaDesativar.cpf}</span>).
              Novas atribuições não aparecerão, mas os históricos antigos continuarão funcionando normalmente. Esta ação pode ser revertida posteriormente reativando o cadastro.
            </span>
          ) : (
            <span>
              Novas atribuições não aparecerão, mas os históricos antigos continuarão funcionando normalmente.
            </span>
          )
        }
        confirmLabel={
          deleteLoading ? (
            <span className="inline-flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
              Desativando...
            </span>
          ) : (
            <span className="inline-flex items-center gap-2">
              <Trash2 className="h-4 w-4 stroke-[1.8]" />
              Sim, desativar colaborador
            </span>
          )
        }
        cancelLabel="Cancelar"
        confirmVariant="danger"
        onConfirm={confirmarDesativacao}
        onCancel={cancelarDesativacao}
        loading={deleteLoading}
      />
    </div>
  );
};

export default Funcionarios;
